// src/Pages/Sessions/PrevSessions.mobile-first.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Button,
  Table,
  Offcanvas,
  Nav,
  Badge,
  ListGroup,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { fetchPatientSessions } from "../../api";
import { UpdateSession } from "../../Components/Sessions/EditSession";
import "./EditSession.css";

export function PrevSessions({ patientId }) {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [activeViewTab, setActiveViewTab] = useState("template");
  const [showPicker, setShowPicker] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const navigate = useNavigate();

  // ------------ helpers

const sortStamp = (s) =>
  new Date(
    s.createdAt ||
    s.sessionsAppointmentStart ||
    s.updatedAt ||
    0
  ).getTime();

const byNewest = (a, b) => sortStamp(b) - sortStamp(a) || String(b._id || "").localeCompare(String(a._id || ""));
 
const patientIdForNav = (s) =>
  String(patientId || s?.sessionsPatientId || "").trim();

  const formatWhen = (iso) => {
    if (!iso) return "Unknown date";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "Unknown date";
    return d.toLocaleString([], {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

const editTabs = useMemo(
    () => (isMobile ? ["template", "notepad", "media"] : ["template", "notepad"]),
    [isMobile]
  );

  // ------------ effects
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    async function fetchSessionsData() {
      try {
        const list = await fetchPatientSessions(patientId);
        const sorted = [...(list || [])].sort(byNewest);
        const makeSid = (s, i) =>
  String(
    s._id ??
    s.id ??
    `sess-${new Date(s.sessionsAppointmentStart || s.createdAt || s.updatedAt || 0).getTime()}-${i}`
  );

const augmented = sorted.map((s, i) => ({
  ...s,
  __sid: makeSid(s, i),
}));

        setSessions(augmented);
      } catch (e) {
        console.error("Error loading sessions", e);
        setSessions([]);
        setSelectedSession(null);
        setSelectedSessionId(null);
      }
    }
    if (patientId) fetchSessionsData();
  }, [patientId]);

  useEffect(() => {
    if (!sessions?.length) {
      setSelectedSession(null);
      setSelectedSessionId(null);
      return;
    }
    // If nothing selected or the selected one no longer exists, pick first
    if (
      !selectedSessionId ||
      !sessions.some((s) => s.__sid === selectedSessionId)
    ) {
      const first = sessions[0];
      setSelectedSession(first);
      setSelectedSessionId(first.__sid);
    }
  }, [sessions, selectedSessionId]);

  // snap to first available tab when session changes
  useEffect(() => {
       if (editTabs.length && !editTabs.includes(activeViewTab)) {
      setActiveViewTab(editTabs[0]);
    }
  }, [editTabs, activeViewTab]);

  const handlePick = (s) => {
    setSelectedSession(s);
    setSelectedSessionId(s.__sid);
    setShowPicker(false);
  };

  const titleFor = (s) =>
   `${s.sessionsServiceName || "—"} • ${formatWhen(
     s.sessionsAppointmentStart || s.createdAt
   )}`;

   // Refresh after save, preserving selection
  const refreshSessionsKeepSelection = async () => {
    const list = await fetchPatientSessions(patientId);
    const sorted = [
      ...(list || []).filter((s) => !!s.sessionsInitial),
      ...(list || [])
        .filter((s) => !s.sessionsInitial)
        .sort((a, b) =>
          new Date(b?.sessionsAppointmentStart || 0) - new Date(a?.sessionsAppointmentStart || 0)
        ),
    ];
    const makeSid = (s, i) =>
  String(
    s._id ??
    s.id ??
    `sess-${new Date(s.sessionsAppointmentStart || s.createdAt || s.updatedAt || 0).getTime()}-${i}`
  );

const augmented = sorted.map((s, i) => ({
  ...s,
  __sid: makeSid(s, i),
}));
    setSessions(augmented);
    // keep same sessionId selected if still present
    if (selectedSession?._id) {
      const again = augmented.find((x) => x._id === selectedSession._id);
      if (again) {
        setSelectedSession(again);
        setSelectedSessionId(again.__sid);
      } else if (augmented[0]) {
        setSelectedSession(augmented[0]);
        setSelectedSessionId(augmented[0].__sid);
      } else {
        setSelectedSession(null);
        setSelectedSessionId(null);
      }
    }
  };

  // ------------ mobile header + tabs
  const MobileHeader = () =>
    selectedSession ? (
      <div
        className="bg-white"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1030,
          borderBottom: "1px solid rgba(0,0,0,.06)",
        }}
      >
        <div className="d-flex align-items-start justify-content-between p-2">
          <div className="me-2">
            <div
              className="fw-semibold text-primary"
              style={{ lineHeight: 1.2 }}
            >
              {selectedSession.sessionsServiceName || "—"}
            </div>
            <div className="text-muted small">
              {formatWhen(selectedSession.sessionsAppointmentStart)}
            </div>
            {!!selectedSession.sessionsInitial && (
              <Badge bg="primary" className="mt-1"></Badge>
            )}
          </div>

          <div className="d-flex gap-2">
            
            <Button
              size="sm"
              variant="primary"
              className="rounded-0"
              onClick={() => setShowPicker(true)}
            >
              Next <i className="fa-solid fa-arrow-right"></i>
            </Button>
          </div>
        </div>

        <Nav
          variant="pills"
          activeKey={activeViewTab}
          onSelect={(k) => setActiveViewTab(k || "template")}
          className="px-2 pb-2"
          style={{ gap: 8 }}
        >
          {editTabs.map((tab) => (
            <Nav.Item key={tab}>
              <Nav.Link eventKey={tab} className="py-1 px-3" style={{ borderRadius: 9999 }}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </div>
    ) : null;

  // ------------ desktop left list
  const DesktopList = () => (
  <Table bordered size="sm" className="table-sm table-fixed">
    <tbody>
      {sessions.length ? (
        sessions.map((s) => {
          const isActive = selectedSessionId === s.__sid;
          return (
            <tr
              key={s.__sid}
              className={isActive ? "table-primary" : "table-light"}
              style={{ cursor: "pointer" }}
              onClick={() => handlePick(s)}
            >
              <td className="small p-2">
                <div className="title-wrap">
                  <div className="title-text">
                    <div
                      className="fw-semibold text-primary line-1"
                      title={titleFor(s)}
                    >
                      {titleFor(s)}
                    </div>
                    <div className="text-muted small title-sub">Edit inline</div>
                  </div>

             
                </div>
              </td>
            </tr>
          );
        })
      ) : (
        <tr>
          <td className="text-muted small">No sessions available.</td>
        </tr>
      )}
    </tbody>
  </Table>
);

  // ------------ content panes
  // Replace your TemplatePane with this version (no Accordion):
  const TemplatePane = () => {
    const sections = Array.isArray(selectedSession?.sections)
      ? selectedSession.sections
      : [];

    if (!sections.length) {
      return <p className="text-muted m-2">No template data available.</p>;
    }

    return (
      <div className="p-2">
        {sections.map((section, index) => {
          const isMulti =
            section.sessionsFieldType === "multiselect" &&
            typeof section.sessionsSectionNotes === "object";

          const content = section.sessionsSectionNotes;

          return (
            <div
              key={index}
              className="bg-white border rounded p-3 shadow-sm mb-2"
            >
              <div className="text-primary fw-semibold mb-2">
                {section.sessionsSection}
              </div>

              {/* Text */}
              {section.sessionsFieldType === "text" && (
                <div className="text-muted">
                  {typeof content === "string"
                    ? content
                    : "" || "No notes available"}
                </div>
              )}

              {/* Textarea */}
              {section.sessionsFieldType === "textarea" && (
                <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                  {typeof content === "string"
                    ? content
                    : "" || "No notes available"}
                </div>
              )}

              {/* Multiselect → chips */}
              {isMulti && Array.isArray(content.selectedOptions) && (
                <div className="d-flex flex-wrap">
                  {content.selectedOptions.length ? (
                    content.selectedOptions.map((opt, i) => (
                      <span
                        key={i}
                        className="me-2 mb-2 px-2 py-1 small"
                        style={{
                          background: "#eef5ff",
                          color: "#0d6efd",
                          borderRadius: 9999,
                        }}
                      >
                        {opt}
                      </span>
                    ))
                  ) : (
                    <div className="text-muted">No options selected.</div>
                  )}
                </div>
              )}

              {/* Image */}
              {section.sessionsFieldType === "image" &&
                section.sessionsImageURL && (
                  <div className="border rounded overflow-hidden">
                    <img
                      src={section.sessionsImageURL}
                      alt={section.sessionsSection}
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: 300,
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </div>
                )}
            </div>
          );
        })}
      </div>
    );
  };

  const NotepadPane = () => {
    if (!selectedSession?.notepadContent?.trim()) {
      return <p className="text-muted m-2">No notepad content available.</p>;
    }
    return (
      <div className="p-2">
        <div className="bg-white border rounded p-3 shadow-sm">
          <div
            className="text-muted"
            dangerouslySetInnerHTML={{ __html: selectedSession.notepadContent }}
          />
        </div>
      </div>
    );
  };

  const MediaPane = () => {
    const files = Array.isArray(selectedSession?.mediaFiles)
      ? selectedSession.mediaFiles
      : [];
    if (!files.length)
      return <p className="text-muted m-2">No media files found.</p>;

    return (
      <div className="p-2">
        <Row className="g-2">
          {files.map((m, idx) => (
            <Col xs={6} sm={4} md={3} key={idx}>
              <div className="border rounded overflow-hidden bg-light">
                {m.fileType === "image" ? (
                  <a href={m.fileUrl} target="_blank" rel="noreferrer">
                    <img
                      src={m.fileUrl}
                      alt={m.fileName}
                      style={{
                        width: "100%",
                        aspectRatio: "4 / 3",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </a>
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center text-muted"
                    style={{ aspectRatio: "4 / 3" }}
                  >
                    <i className="fas fa-file fa-2x" />
                  </div>
                )}
                <div className="px-2 py-1 bg-white">
                  <small className="text-truncate d-block">{m.fileName}</small>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

const tabToSection = { template: "apptTemplate", notepad: "apptNotepad", media: "apptMedia" };
const Content = () => {
    if (!selectedSession) return <p className="m-2">Select a session to edit.</p>;
    const pid = patientIdForNav(selectedSession);
    const sectionKey = tabToSection[activeViewTab] || "apptTemplate";
    return (
      <UpdateSession
        inline                 // 👈 stay on this page after save
        sessionId={selectedSession._id}
        patientId={pid}
        appointmentId={selectedSession.sessionsAppointmentId || "adhoc"}
        serviceName={selectedSession.sessionsServiceName || "Ad Hoc"}
        appointmentStart={selectedSession.sessionsAppointmentStart || selectedSession.createdAt}
        practiceId={selectedSession.sessionsPracticeId}
        resourceId={selectedSession.sessionsResourceId}   // may be undefined; safe
        sections={selectedSession.sections || []}
        notepadContentR={selectedSession.notepadContent || ""}
        notepadContentType={selectedSession.notepadContentType || "html"}
        templateId={selectedSession.sessionsTemplateId || ""}
        mediaFilesR={selectedSession.mediaFiles || []}
        activeSection={sectionKey}
        onSaved={refreshSessionsKeepSelection}
      />
    );
  };

  // ------------ Offcanvas picker (mobile)
  const SessionPicker = () => (
    <Offcanvas
      show={showPicker}
      onHide={() => setShowPicker(false)}
      placement="bottom"
      backdrop
      scroll
      className="sessions-drawer"
      style={{ height: "50vh" }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="text-primary">
          Choose a Session
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <ListGroup variant="flush">
          {sessions.length ? (
            sessions.map((s) => {
              const isActive = selectedSessionId === s.__sid;
              return (
                // Offcanvas picker
                <ListGroup.Item
                  key={s.__sid}
                  action
                  onClick={() => handlePick(s)}
                  className={`d-flex justify-content-between align-items-center ${
                    isActive ? "bg-primary bg-opacity-10" : ""
                  }`}
                >
                  <div className="me-2">
                    <div
                      className="fw-semibold text-primary text-truncate"
                      title={titleFor(s)}
                    >
                      {s.sessionsServiceName || "—"}
                    </div>
                    <div className="text-muted small">
                      {formatWhen(s.sessionsAppointmentStart || s.createdAt)}
                    </div>
                  </div>
                  {s.sessionsInitial && <Badge bg="primary"></Badge>}
                </ListGroup.Item>
              );
            })
          ) : (
            <div className="text-muted small">No sessions available.</div>
          )}
        </ListGroup>
      </Offcanvas.Body>
    </Offcanvas>
  );

  // ------------ render
  if (isMobile) {
    return (
      <div className="pb-3">
        <MobileHeader />
        <Content />
        <SessionPicker />
      </div>
    );
  }

  // Desktop (unchanged 2-column experience)
  return (
    <div>
      <Row className="align-items-center ms-2">
        <Col md={3} className="d-flex flex-column">
          <h6 className="text-primary mb-0">SESSIONS</h6>
        </Col>
        <Col md={6}>
          <div className="d-flex justify-content-start align-items-center gap-3">
            {editTabs.map((tab) => (
              <Button
                key={tab}
                variant=""
                size="sm"
                
                className={`ms-5 fs-6 px-0 w-25 bg-transparent border-0 rounded-0 d-inline-block ${
                  activeViewTab === tab
                    ? "border-bottom border-primary border-2 text-primary"
                    : "text-primary"

                }`}
                onClick={() => setActiveViewTab(tab)}
                style={{
                  fontWeight: activeViewTab === tab ? "bold" : "normal",
                  lineHeight: "1.2",
                }}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </Button>
            ))}
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={3} className="d-flex justify-content-start">
          <p className="text-muted small mb-0 ms-4">
            {sessions.length > 0
              ? `${sessions.length} appointment(s)`
              : "No sessions available"}
          </p>
        </Col>
      </Row>

      {sessions.length > 0 ? (
        <Row className="g-3 prev-sessions-row mb-2">
          {/* was ms-1 mb-2 */}
          <Col md={3} className="left-col position-sticky" style={{ top: 82 }}>
            <DesktopList />
          </Col>

          <Col md={9} className="right-col position-relative">
            <div className="right-scroll">
              <Content />
            </div>
          </Col>
        </Row>
      ) : (
        <Row className="text-center mt-3">
          <Col>
            <p className="text-muted">
              No sessions available for this patient.
            </p>
          </Col>
        </Row>
      )}
    </div>
  );
}
